import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import React, { useRef, forwardRef } from "react";
import { useSwipeable } from "react-swipeable";

interface HorizontalScrollProps {
  children: React.ReactNode;
  className?: string;
}

const HorizontalScroll = forwardRef<HTMLDivElement, HorizontalScrollProps>(
  ({ children, className = "" }, forwardedRef) => {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const swipeThreshold = 250; // Minimum distance needed to trigger tab switch when at start
    const isTouchDevice = useIsTouchDevice();

    const handleTouchEvent = (e: React.TouchEvent) => {
      const container = scrollRef.current;
      if (!container) return;

      const isAtStart = container.scrollLeft <= 0;
      const isAtEnd =
        container.scrollLeft + container.clientWidth >= container.scrollWidth;

      if (!isAtStart || !isAtEnd) {
        e.stopPropagation();
      }
    };

    const swipeHandlers = useSwipeable({
      onSwiping: (e) => {
        const container = scrollRef.current;
        if (!container) return;

        const isAtStart = container.scrollLeft <= 0;

        // Only stop propagation if we're not at the start or if we're at the start but haven't exceeded the threshold
        if (!isAtStart || (isAtStart && Math.abs(e.deltaX) < swipeThreshold)) {
          e.event.stopPropagation();
        }
      },
      trackMouse: true,
      trackTouch: true,
    });

    const { ref: swipeRef, ...restSwipeHandlers } = swipeHandlers;

    return (
      <div
        className={`overflow-x-auto ${
          isTouchDevice ? "" : "custom-scrollbar"
        } ${className}`}
        ref={(el) => {
          scrollRef.current = el;
          if (typeof forwardedRef === "function") forwardedRef(el);
          else if (forwardedRef) forwardedRef.current = el;
          if (typeof swipeRef === "function") swipeRef(el);
        }}
        onTouchStart={handleTouchEvent}
        onTouchMove={handleTouchEvent}
        onTouchEnd={handleTouchEvent}
        {...restSwipeHandlers}
      >
        {children}
      </div>
    );
  }
);

HorizontalScroll.displayName = "HorizontalScroll";

export default HorizontalScroll;
